import { useState } from 'react';
import { useRange, UseRangeProps } from 'react-instantsearch';
import { useTranslation } from 'next-i18next';

import styles from './RangeInput.module.scss';

const unsetNumberInputValue = '';

function stripLeadingZeroFromInput(value: string): string {
  return value.replace(/^(0+)\d/, (part) => Number(part).toString());
}

export const RangeInput = ({
  attribute,
  min,
  max,
  precision,
}: UseRangeProps) => {
  const { t } = useTranslation('searchResults');
  const { start, range, canRefine, refine } = useRange({
    attribute,
    min,
    max,
    precision,
  });
  const step = 1 / Math.pow(10, precision || 0);
  const values = {
    min:
      start[0] !== -Infinity && start[0] !== range.min
        ? start[0]
        : unsetNumberInputValue,
    max:
      start[1] !== Infinity && start[1] !== range.max
        ? start[1]
        : unsetNumberInputValue,
  };
  const [prevValues, setPrevValues] = useState(values);

  const [{ from, to }, setRange] = useState({
    from: values.min?.toString(),
    to: values.max?.toString(),
  });

  if (values.min !== prevValues.min || values.max !== prevValues.max) {
    setRange({ from: values.min?.toString(), to: values.max?.toString() });
    setPrevValues(values);
  }

  const ensureCorrectNumericalOrder = () => {
    if (parseInt(to || '') < parseInt(from || '')) {
      setRange({ to: from, from });
    }
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();

        refine([from ? Number(from) : undefined, to ? Number(to) : undefined]);
      }}
      className={styles.rangeInputForm}
    >
      <label className={styles.rangeInputLabel}>
        $
        <input
          type="number"
          min={range.min}
          max={range.max}
          value={stripLeadingZeroFromInput(from || unsetNumberInputValue)}
          step={step}
          placeholder={range.min?.toString()}
          disabled={!canRefine}
          onInput={({ currentTarget }) => {
            const value = currentTarget.value;

            setRange({ from: value || unsetNumberInputValue, to });
          }}
          onBlur={ensureCorrectNumericalOrder}
          className={styles.rangeInputInput}
        />
      </label>
      <span className={styles.rangeInputSeparator}>-</span>
      <label className={styles.rangeInputLabel}>
        $
        <input
          type="number"
          min={min}
          max={max}
          value={stripLeadingZeroFromInput(to || unsetNumberInputValue)}
          step={step}
          placeholder={range.max?.toString()}
          disabled={!canRefine}
          onInput={({ currentTarget }) => {
            const value = currentTarget.value;

            setRange({ from, to: value || unsetNumberInputValue });
          }}
          onBlur={ensureCorrectNumericalOrder}
          className={styles.rangeInputInput}
        />
      </label>
      <button type="submit" className={styles.rangeSubmitButton}>
        {t('rangeInput.submit')}
      </button>
    </form>
  );
};
